/**
 * @name ModalHistorico
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal para exibir histórico de movimentações de créditos
 * @package components/pages/gestao-financeira/creditos/components
 */

import { Close as CloseIcon, GetApp as ExportIcon, History as HistoryIcon } from '@mui/icons-material';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React from 'react';
import useHistoricoData from '../../historico/useHistoricoData';

interface ModalHistoricoProps {
     open: boolean;
     onClose: () => void;
}

/**
 * Modal do histórico de movimentações
 * Reutiliza a lógica do useHistoricoData
 */
const ModalHistorico: React.FC<ModalHistoricoProps> = ({ open, onClose }) => {
     const { movimentacoes, loading, filtros, metricas, updateFiltros, recarregar, exportarDados } = useHistoricoData();

     const getTipoColor = (tipo: string | null) => {
          return tipo === 'credito' ? 'success' : 'error';
     };

     const formatCurrency = (value: number | null) => {
          if (value === null || value === undefined) return 'R$ 0,00';
          return new Intl.NumberFormat('pt-BR', {
               style: 'currency',
               currency: 'BRL',
          }).format(value);
     };

     return (
          <>
               <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth='lg'
                    fullWidth
                    PaperProps={{ sx: { height: '90vh' } }}
               >
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                         <Box>
                              <Typography
                                   variant='h6'
                                   sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                   <HistoryIcon />
                                   Histórico de Movimentações Financeiras
                              </Typography>

                              <Typography
                                   variant='subtitle1'
                                   sx={{ marginLeft: 5, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}
                              >
                                   Créditos e débitos, sem consumo da cantina.
                              </Typography>
                         </Box>
                         <IconButton
                              aria-label='close'
                              onClick={onClose}
                              sx={{
                                   position: 'absolute',
                                   right: 8,
                                   top: 8,
                                   color: (theme) => theme.palette.grey[500],
                              }}
                         >
                              <CloseIcon />
                         </IconButton>
                    </DialogTitle>

                    <DialogContent
                         dividers
                         sx={{ p: 0 }}
                    >
                         {/* Filtros */}
                         <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                   <TextField
                                        select
                                        label='Tipo'
                                        value={filtros.tipo}
                                        onChange={(e) => updateFiltros({ tipo: e.target.value as any })}
                                        size='small'
                                        sx={{ minWidth: 120 }}
                                   >
                                        <MenuItem value=''>Todos</MenuItem>
                                        <MenuItem value='credito'>Crédito</MenuItem>
                                        <MenuItem value='debito'>Débito</MenuItem>
                                   </TextField>

                                   <TextField
                                        select
                                        label='Período'
                                        value={filtros.periodo}
                                        onChange={(e) => updateFiltros({ periodo: e.target.value as any })}
                                        size='small'
                                        sx={{ minWidth: 150 }}
                                   >
                                        <MenuItem value='30'>Últimos 30 dias</MenuItem>
                                        <MenuItem value='90'>Últimos 90 dias</MenuItem>
                                        <MenuItem value='180'>Últimos 6 meses</MenuItem>
                                        <MenuItem value='365'>Último ano</MenuItem>
                                   </TextField>

                                   <Button
                                        variant='outlined'
                                        onClick={recarregar}
                                        disabled={loading}
                                        size='small'
                                   >
                                        Atualizar
                                   </Button>

                                   <Button
                                        variant='contained'
                                        startIcon={<ExportIcon />}
                                        onClick={exportarDados}
                                        disabled={loading || movimentacoes.length === 0}
                                        size='small'
                                        color='secondary'
                                   >
                                        Exportar
                                   </Button>
                              </Box>
                         </Box>

                         {/* Saldo Líquido em destaque */}
                         <Box sx={{ m: 2, p: 2, bgcolor: 'primary.50', mb: 2, borderRadius: 2, border: '2px solid', borderColor: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                   <Typography
                                        variant='subtitle2'
                                        color='primary.main'
                                        fontWeight='bold'
                                   >
                                        Saldo Líquido
                                   </Typography>
                                   <Typography
                                        variant='h4'
                                        color='primary.main'
                                        fontWeight='bold'
                                        sx={{ lineHeight: 1.1 }}
                                   >
                                        {formatCurrency(metricas.totalCreditos - metricas.totalDebitos)}
                                   </Typography>
                                   <Typography
                                        variant='caption'
                                        color='text.secondary'
                                   >
                                        (Total de Créditos - Total de Débitos)
                                   </Typography>
                              </Box>
                         </Box>

                         {/* Estatísticas */}
                         <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                   <Box>
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                        >
                                             Total de Créditos
                                        </Typography>
                                        <Typography
                                             variant='h6'
                                             color='success.main'
                                             fontWeight='bold'
                                        >
                                             {formatCurrency(metricas.totalCreditos)}
                                        </Typography>
                                   </Box>
                                   <Box>
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                        >
                                             Total de Débitos
                                        </Typography>
                                        <Typography
                                             variant='h6'
                                             color='error.main'
                                             fontWeight='bold'
                                        >
                                             {formatCurrency(metricas.totalDebitos)}
                                        </Typography>
                                   </Box>

                                   <Box>
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                        >
                                             Total de Movimentações
                                        </Typography>
                                        <Typography
                                             variant='h6'
                                             fontWeight='bold'
                                        >
                                             {metricas.totalMovimentacoes}
                                        </Typography>
                                   </Box>
                              </Box>
                         </Box>

                         {/* Tabela */}
                         <TableContainer
                              component={Paper}
                              sx={{ maxHeight: 'calc(90vh - 300px)' }}
                         >
                              <Table
                                   stickyHeader
                                   size='small'
                              >
                                   <TableHead>
                                        <TableRow>
                                             <TableCell>Data</TableCell>
                                             <TableCell>Pessoa</TableCell>
                                             <TableCell>Tipo</TableCell>
                                             <TableCell>Descrição</TableCell>
                                             <TableCell align='right'>Valor</TableCell>
                                             <TableCell align='right'>Saldo Anterior</TableCell>
                                             <TableCell align='right'>Saldo Posterior</TableCell>
                                        </TableRow>
                                   </TableHead>
                                   <TableBody>
                                        {movimentacoes.map((movimentacao, index) => (
                                             <TableRow
                                                  key={movimentacao.id || index}
                                                  hover
                                             >
                                                  <TableCell>{movimentacao.data_movimentacao ? new Date(movimentacao.data_movimentacao).toLocaleDateString('pt-BR') : 'N/A'}</TableCell>
                                                  <TableCell>
                                                       <Box>
                                                            <Typography variant='body2'>{movimentacao.pessoa}</Typography>
                                                            <Typography
                                                                 variant='caption'
                                                                 color='text.secondary'
                                                            >
                                                                 {movimentacao.tipo_pessoa}
                                                            </Typography>
                                                       </Box>
                                                  </TableCell>
                                                  <TableCell>
                                                       <Chip
                                                            label={movimentacao.tipo_movimentacao}
                                                            color={getTipoColor(movimentacao.tipo_movimentacao)}
                                                            size='small'
                                                       />
                                                  </TableCell>
                                                  <TableCell>
                                                       <Typography variant='body2'>{movimentacao.descricao}</Typography>
                                                       <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                       >
                                                            {movimentacao.tipo_origem}
                                                       </Typography>
                                                  </TableCell>
                                                  <TableCell align='right'>
                                                       <Typography
                                                            color={getTipoColor(movimentacao.tipo_movimentacao)}
                                                            fontWeight='medium'
                                                       >
                                                            {movimentacao.tipo_movimentacao === 'credito' ? '+' : '-'}
                                                            {formatCurrency(movimentacao.valor)}
                                                       </Typography>
                                                  </TableCell>
                                                  <TableCell align='right'>{formatCurrency(movimentacao.saldo_anterior)}</TableCell>
                                                  <TableCell align='right'>{formatCurrency(movimentacao.saldo_posterior)}</TableCell>
                                             </TableRow>
                                        ))}

                                        {movimentacoes.length === 0 && (
                                             <TableRow>
                                                  <TableCell
                                                       colSpan={7}
                                                       align='center'
                                                       sx={{ py: 3 }}
                                                  >
                                                       <Typography color='text.secondary'>Nenhuma movimentação encontrada</Typography>
                                                  </TableCell>
                                             </TableRow>
                                        )}
                                   </TableBody>
                              </Table>
                         </TableContainer>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                         <Button
                              onClick={onClose}
                              variant='outlined'
                         >
                              Fechar
                         </Button>
                    </DialogActions>
               </Dialog>
          </>
     );
};

export default ModalHistorico;
